import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiServiceInstanceDashboard } from '../apiUtils';
import {
  CountsData,
  EBData,
  TurfData,
  ProbeAnalysisData,
  ProbeCompareData,
  ProbeItemThemesData,
} from '../Reducers/maxdiffAnalysisReducer';

// All MaxDiff analysis thunks live here. New sub-features (choice
// probability, HB, TURF, ...) get their own thunks in this file rather
// than a new one — keeps the import surface small and matches how
// aiAnalysis.ts groups the entire qual analysis surface.

// -------------------------- Filter payload --------------------------
// MaxDiff analysis was originally wired with a pre-resolved
// `filterUserIds` list. That diverged from every other question type,
// where LeftPane.tsx spreads `{...selectedFilters, questionObject}` and
// the backend resolves to user_ids internally (see AnalysisGet.post
// canonical pattern). The `filters` + `questionObject` fields below
// bring MaxDiff into line — same shapes, same backend contract.
// `filterUserIds` is kept as a deprecated fallback for one release; it
// will be removed alongside the backend cleanup PR.

// Demographic filters — same slice used by the standard analysis path
// (state.dashboardReducer.selectedFilters).
export type SelectedFilters = {
  age?: string[];
  gender?: string[];
  state?: string[];
  city?: string[];
  country?: string[];
};

// Cross-question filters — shape matches state.dashboardReducer.selectedComparisonQns.
export type ComparisonQn = {
  questionId: number | string;
  answerType: string;
  answer: any;
};

// Common fields every MaxDiff thunk accepts. Extracted so component
// callers can build one object and hand it to any of the fetch* thunks
// without repeating the shape.
export type MaxDiffFilterArgs = {
  filters?: SelectedFilters | null;
  questionObject?: ComparisonQn[] | null;
  // Deprecated: pre-resolved list. Kept for one release; the backend
  // reads canonical filters first and falls back to this if unset.
  filterUserIds?: number[] | null;
};

// Deterministic cache-key fragment for the filter dimension. Sorted so
// {gender:['M','F']} and {gender:['F','M']} hit the same key; sorted
// questionObject by questionId for the same reason. Kept lightweight —
// used inside Redux reducers so it must run on every dispatch.
export const buildMaxDiffFilterHash = (
  filters?: SelectedFilters | null,
  questionObject?: ComparisonQn[] | null,
): string => {
  const f = filters
    ? JSON.stringify(
        Object.keys(filters)
          .sort()
          .reduce((acc: Record<string, string[]>, k) => {
            const v = (filters as any)[k];
            if (Array.isArray(v) && v.length > 0) {
              acc[k] = [...v].sort();
            }
            return acc;
          }, {}),
      )
    : '';
  const q = questionObject && questionObject.length > 0
    ? JSON.stringify(
        [...questionObject].sort(
          (a, b) => Number(a.questionId) - Number(b.questionId),
        ),
      )
    : '';
  return `${f}|${q}` || 'all';
};

// Assemble the canonical filter part of a MaxDiff request body. Mirrors
// LeftPane.tsx's spread pattern — demographic fields at the top level,
// cross-question filters under `questionObject`. Legacy `filterUserIds`
// forwarded for backwards-compat until the backend cleanup PR.
const buildFilterPayload = (args: MaxDiffFilterArgs) => {
  const { filters, questionObject, filterUserIds } = args;
  return {
    ...(filters || {}),
    questionObject: questionObject && questionObject.length > 0
      ? questionObject
      : undefined,
    // Backend uses ast.literal_eval on filterUserIds, so send a
    // stringified Python-literal-compatible list ("[1, 2, 3]").
    // JSON.stringify produces the same syntax for lists of numbers.
    filterUserIds: filterUserIds
      ? JSON.stringify(filterUserIds)
      : undefined,
  };
};

// -------------------------- Thunks --------------------------

export const fetchMaxDiffCounts = createAsyncThunk<
  CountsData,
  {
    batchId: number | string;
    questionId: number | string;
    languageId?: string;
  } & MaxDiffFilterArgs
>('maxdiffAnalysis/fetchCounts', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/counts/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
      ...buildFilterPayload(payload),
    },
  );
  return res.data?.data as CountsData;
});

export const fetchMaxDiffEmpiricalBayes = createAsyncThunk<
  EBData,
  {
    batchId: number | string;
    questionId: number | string;
    languageId?: string;
    // Advanced overrides — leave undefined to use backend defaults
    // (k=1.0, iters=500).
    shrinkageK?: number;
    bootstrapIters?: number;
  } & MaxDiffFilterArgs
>('maxdiffAnalysis/fetchEmpiricalBayes', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/empirical_bayes/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
      shrinkageK: payload.shrinkageK,
      bootstrapIters: payload.bootstrapIters,
      ...buildFilterPayload(payload),
    },
  );
  return res.data?.data as EBData;
});

export const fetchMaxDiffProbeAnalysis = createAsyncThunk<
  ProbeAnalysisData,
  {
    batchId: number | string;
    questionId: number | string;
    languageId?: string;
  } & MaxDiffFilterArgs
>('maxdiffAnalysis/fetchProbeAnalysis', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/probe_analysis/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
      ...buildFilterPayload(payload),
    },
  );
  return res.data?.data as ProbeAnalysisData;
});

export const fetchMaxDiffProbeCompare = createAsyncThunk<
  { key: string; data: ProbeCompareData },
  {
    batchId: number | string;
    questionId: number | string;
    // Item ids in the caller's selection order — the reducer sorts them
    // before hashing for the cache key so re-selecting the same set in
    // a different order hits the cache.
    itemIds: number[];
    // Precomputed cache key (`${sortedIds.join(',')}:${filterHash}`).
    // Passed in so the pending/rejected reducers can use it too — the
    // thunk fulfilment payload doesn't get to see meta.arg after the
    // fulfilled action fires.
    cacheKey: string;
    languageId?: string;
    // Bypasses the server-side persistent cache. Wired to the compare
    // view's "Regenerate" button.
    forceRefresh?: boolean;
  } & MaxDiffFilterArgs
>('maxdiffAnalysis/fetchProbeCompare', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/probe_compare/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      // Backend uses ast.literal_eval — a JSON-stringified list of ints
      // parses cleanly as a Python literal.
      itemIds: JSON.stringify(payload.itemIds),
      languageId: payload.languageId || 'en',
      forceRefresh: payload.forceRefresh ? true : undefined,
      ...buildFilterPayload(payload),
    },
  );
  return {
    key: payload.cacheKey,
    data: res.data?.data as ProbeCompareData,
  };
});

export const fetchMaxDiffProbeItemThemes = createAsyncThunk<
  { key: string; data: ProbeItemThemesData },
  {
    batchId: number | string;
    questionId: number | string;
    // Same cache-key pattern as fetchMaxDiffProbeCompare — passed in
    // so the pending/rejected reducers can use it too.
    cacheKey: string;
    languageId?: string;
    // Bypasses the server-side persistent cache. Wired to the theme
    // strip's "Regenerate" button.
    forceRefresh?: boolean;
  } & MaxDiffFilterArgs
>('maxdiffAnalysis/fetchProbeItemThemes', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/probe_item_themes/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
      forceRefresh: payload.forceRefresh ? true : undefined,
      ...buildFilterPayload(payload),
    },
  );
  return {
    key: payload.cacheKey,
    data: res.data?.data as ProbeItemThemesData,
  };
});

export const fetchMaxDiffTurf = createAsyncThunk<
  TurfData,
  {
    batchId: number | string;
    questionId: number | string;
    languageId?: string;
    itemsPool?: number[] | null;   // null / undefined = all items
    comboSize?: number;
    topN?: number;
    appealMode?: 'best' | 'positive_net';
  } & MaxDiffFilterArgs
>('maxdiffAnalysis/fetchTurf', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/turf/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
      itemsPool: payload.itemsPool && payload.itemsPool.length > 0
        ? JSON.stringify(payload.itemsPool)
        : undefined,
      comboSize: payload.comboSize,
      topN: payload.topN,
      appealMode: payload.appealMode,
      ...buildFilterPayload(payload),
    },
  );
  return res.data?.data as TurfData;
});

export type ProbeQuestionSlot = {
  questionId: number;
  questionText: string;
  answerType: string;
};

// Resolves a MaxDiff question to the derived per-slot AVT questions that hold
// its two open-ends. Each probe slot is stored as its own AVT question, so the
// Top / Bottom tabs can mount the standard AI-analysis pane against the id
// returned here with no MaxDiff-specific handling in that pane.
//
// Not filter-aware on purpose: this is question identity, not data. The pane
// applies the dashboard filters itself, exactly as it does for a real AVT
// question.
export const fetchMaxDiffProbeQuestions = createAsyncThunk<
  Record<string, ProbeQuestionSlot>,
  {
    batchId: number | string;
    questionId: number | string;
    languageId?: string;
  }
>('maxdiffAnalysis/fetchProbeQuestions', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/probe_questions/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
    },
  );
  return (res.data?.data?.slots || {}) as Record<string, ProbeQuestionSlot>;
});
